import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Users } from "lucide-react";

import { EmptyState } from "@/components/empty-state";
import { StoryCard, StoryCardSkeleton } from "@/components/story-card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import { fetchFollowingStories } from "@/lib/queries";

export const Route = createFileRoute("/_authenticated/following")({
  head: () => ({
    meta: [
      { title: "Following — Quibblies" },
      {
        name: "description",
        content: "The latest published stories from the authors you follow on Quibblies.",
      },
      { property: "og:title", content: "Following — Quibblies" },
      { property: "og:description", content: "New work from authors you follow." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: FollowingPage,
});

function FollowingPage() {
  const { user } = useAuth();
  const feed = useQuery({
    queryKey: ["following-feed", user?.id],
    queryFn: () => fetchFollowingStories(user!.id),
    enabled: Boolean(user),
  });

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
      <h1 className="font-display text-4xl text-foreground">Following</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        New work from the authors you follow.
      </p>

      <div className="mt-8">
        {feed.isLoading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <StoryCardSkeleton key={index} />
            ))}
          </div>
        ) : (feed.data ?? []).length === 0 ? (
          <EmptyState
            icon={Users}
            title="No stories from your circle yet"
            description="Follow a few authors and their new releases will land here."
            action={
              <Button asChild variant="soft">
                <Link to="/discover">Browse authors</Link>
              </Button>
            }
          />
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {feed.data!.map((story) => (
              <StoryCard key={story.id} story={story} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
